import type { Map as MapboxMap, MapMouseEvent, Point } from 'mapbox-gl';
import { type RefObject, useEffect, useRef } from 'react';
import { formatNumber } from '@/libs/utils/format';
import { registerMapEvent } from '@/libs/utils/registerMapEvents';
import { LayerId, MAP_BUBBLE_LABEL, MAP_BUBBLE_SURFACE, MAP_BUBBLE_TITLE } from '../constants';
import { positionMapTooltip } from '../utils/positionMapTooltip';
import { readMvtNumber } from '../utils/readMvtNumber';

const UNNAMED_ROAD = 'Unnamed road';

/**
 * Hover-to-read AADT on the map-level traffic tiles. Lives at the same level as
 * the layer, so it works with no location selected. `mapId` rebinds the
 * listeners when the map instance is replaced, like the layer effect it pairs
 * with.
 */
export function useTrafficTilesHover(
  map: RefObject<MapboxMap | null>,
  enabled: boolean,
  mapId: string,
) {
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; rebind on enabled/mapId
  useEffect(() => {
    const currentMap = map.current;
    if (!currentMap || !enabled) return;

    function getOrCreateTooltip(hostMap: MapboxMap): HTMLDivElement {
      if (!tooltipRef.current) {
        const tooltip = document.createElement('div');
        // The bubble surface the zone bubble and the station popup wear.
        tooltip.className = `absolute z-10 pointer-events-none flex w-max max-w-56 flex-col ${MAP_BUBBLE_SURFACE}`;
        tooltip.style.display = 'none';
        hostMap.getContainer().appendChild(tooltip);
        tooltipRef.current = tooltip;
      }
      return tooltipRef.current;
    }

    function hideTooltip() {
      if (tooltipRef.current) tooltipRef.current.style.display = 'none';
    }

    // Built from DOM nodes with textContent, never innerHTML: road_name is
    // interpolated feature data.
    function renderTooltip(hostMap: MapboxMap, roadName: string, aadt: number, point: Point) {
      const tooltip = getOrCreateTooltip(hostMap);
      tooltip.replaceChildren();

      const road = document.createElement('span');
      road.className = MAP_BUBBLE_TITLE;
      road.textContent = roadName;

      const traffic = document.createElement('span');
      traffic.className = `mt-1.5 ${MAP_BUBBLE_LABEL}`;
      traffic.textContent = `Avg. daily traffic: ${formatNumber(aadt)}`;

      tooltip.append(road, traffic);
      tooltip.style.display = 'flex';
      positionMapTooltip({
        tooltip,
        container: hostMap.getContainer(),
        point,
        // Measured, not fixed: this tooltip sizes to its text (`w-max`).
        size: { width: tooltip.offsetWidth, height: tooltip.offsetHeight },
        placement: 'below-right',
      });
    }

    function handleMouseMove(event: MapMouseEvent) {
      const hostMap = map.current;
      if (!hostMap) return;

      // With a location selected, LayerId.TRAFFIC_MAX_AADT is painted on top and
      // its useMapInteractions tooltip is richer, so it wins.
      if (
        hostMap.getLayer(LayerId.TRAFFIC_MAX_AADT) &&
        hostMap.queryRenderedFeatures(event.point, { layers: [LayerId.TRAFFIC_MAX_AADT] }).length
      ) {
        hideTooltip();
        return;
      }

      // The binding is layer-scoped, so Mapbox already hit-tested the tiles.
      const properties = event.features?.at(0)?.properties;
      const aadt = readMvtNumber(properties?.aadt);
      if (aadt === undefined) {
        hostMap.getCanvas().style.cursor = '';
        hideTooltip();
        return;
      }

      hostMap.getCanvas().style.cursor = 'pointer';
      const roadName = properties?.road_name;
      renderTooltip(
        hostMap,
        typeof roadName === 'string' && roadName.length ? roadName : UNNAMED_ROAD,
        aadt,
        event.point,
      );
    }

    function handleMouseLeave() {
      const canvas = map.current?.getCanvas?.();
      if (canvas) canvas.style.cursor = '';
      hideTooltip();
    }

    const cleanupMouseMove = registerMapEvent(
      currentMap,
      'mousemove',
      LayerId.TRAFFIC_TILES,
      handleMouseMove,
    );
    const cleanupMouseLeave = registerMapEvent(
      currentMap,
      'mouseleave',
      LayerId.TRAFFIC_TILES,
      handleMouseLeave,
    );

    return () => {
      cleanupMouseMove();
      cleanupMouseLeave();

      tooltipRef.current?.remove();
      tooltipRef.current = null;

      // useMap calls map.remove() first on unmount, and a removed map has no canvas.
      const canvas = currentMap.getCanvas?.();
      if (canvas) canvas.style.cursor = '';
    };
  }, [enabled, mapId]);
}
